// Сменные чек-листы линии (модуль quality-records): записи по станциям и
// датам, замечания, «Подтвердить».  Заполняют на мобилке.
// Чтение — manufacturing_order.read или stage.execute, подтверждение — quality.manage.

import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { useQualityError } from "@/components/OrderNonconformities";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { qualityApi, stationsApi, type ShiftChecklist } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const FIELD_CLS = "px-3 py-2 rounded-md bg-surface border border-border";

export default function ShiftChecklists() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const errText = useQualityError();
  const { hasPermission } = useAuth();
  const canAck = hasPermission("quality.manage");
  const [stationId, setStationId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [needsAck, setNeedsAck] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);

  const stations = useQuery({ queryKey: ["stations", "all"], queryFn: () => stationsApi.list(), staleTime: 60_000 });
  const list = useQuery({
    queryKey: ["quality", "shift-checklists", { stationId, from, to, needsAck, page }],
    queryFn: () =>
      qualityApi.shiftChecklists({
        station_id: stationId ? Number(stationId) : undefined,
        from: from || undefined,
        to: to || undefined,
        needs_ack: needsAck || undefined,
        page,
        page_size: 50,
      }),
  });
  const ack = useMutation({
    mutationFn: (id: string) => qualityApi.ackShiftChecklist(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quality", "shift-checklists"] }),
  });
  const filter = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setPage(1);
  };

  return (
    <>
      <PageHeader eyebrow={t("quality.eyebrow")} title={t("quality.sc.title")} description={t("quality.sc.description")} />

      <div className="card p-3 mt-3 flex flex-wrap items-center gap-3 text-sm">
        <select className={FIELD_CLS} value={stationId} onChange={(e) => filter(setStationId)(e.target.value)}>
          <option value="">{t("quality.sc.allStations")}</option>
          {(stations.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} · {s.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2">
          {t("quality.sc.from")}
          <input className={FIELD_CLS} type="date" value={from} onChange={(e) => filter(setFrom)(e.target.value)} />
        </label>
        <label className="flex items-center gap-2">
          {t("quality.sc.to")}
          <input className={FIELD_CLS} type="date" value={to} onChange={(e) => filter(setTo)(e.target.value)} />
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={needsAck} onChange={(e) => filter(setNeedsAck)(e.target.checked)} />
          {t("quality.sc.needsAck")}
        </label>
      </div>

      {ack.isError && <ErrorBanner onDismiss={() => ack.reset()}>{errText(ack.error)}</ErrorBanner>}
      {list.isLoading && <LoadingState />}
      {list.isError && <ErrorState error={list.error} />}
      {list.data && list.data.items.length === 0 && (
        <div className="card p-8 mt-4 text-center text-text-muted">{t("quality.sc.empty")}</div>
      )}

      {list.data && list.data.items.length > 0 && (
        <section className="card mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt text-text-muted">
              <tr>
                <th className="px-3 py-2 text-left">{t("quality.sc.colDate")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.station")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.colForm")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.colDecision")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.colRemarks")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.colFilled")}</th>
                <th className="px-3 py-2 text-left">{t("quality.sc.colAck")}</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((r) => (
                <Fragment key={r.id}>
                  <tr className="border-t border-border cursor-pointer hover:bg-surface" onClick={() => setOpen(open === r.id ? null : r.id)}>
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      {r.shift_date} · {t("quality.sc.shift", { n: r.shift_no })}
                    </td>
                    <td className="px-3 py-2">{r.station_code}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.form}</td>
                    <td className="px-3 py-2">
                      <span className={`pill text-xs ${r.decision === "ALLOWED" ? "pill-ok" : "pill-danger"}`}>
                        {t(`quality.decision.${r.decision}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Remarks r={r} />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.filler_name ?? "—"}
                      <span className="text-text-muted block">{formatDateTime(r.filled_at)}</span>
                    </td>
                    <td className="px-3 py-2 text-xs whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {r.ack_at ? (
                        <>
                          {r.ack_name ?? "—"}
                          <span className="text-text-muted block">{formatDateTime(r.ack_at)}</span>
                        </>
                      ) : r.needs_ack && canAck ? (
                        <button className="btn btn-sm btn-primary" disabled={ack.isPending} onClick={() => ack.mutate(r.id)}>
                          {t("quality.sc.ack")}
                        </button>
                      ) : r.needs_ack ? (
                        <span className="pill pill-warn text-xs">{t("quality.sc.waitingAck")}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                  {open === r.id && (
                    <tr className="bg-surface">
                      <td colSpan={7} className="px-3 py-3">
                        <ul className="space-y-1">
                          {r.items.map((i) => (
                            <li key={i.code} className="grid grid-cols-[24px,1fr,1fr,1fr] gap-2">
                              <span className={i.ok ? "text-ok" : "text-danger"}>{i.ok ? "✓" : "✗"}</span>
                              <span>{i.object}</span>
                              <span className="text-text-muted">{i.criterion}</span>
                              <span>{i.note ?? ""}</span>
                            </li>
                          ))}
                        </ul>
                        {r.remarks && <p className="mt-2">{r.remarks}</p>}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {list.data && list.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", {
              page: list.data.pagination.page,
              pages: list.data.pagination.pages,
              total: list.data.pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button className="btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ←
            </button>
            <button className="btn" disabled={page >= list.data.pagination.pages} onClick={() => setPage((p) => p + 1)}>
              →
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Remarks({ r }: { r: ShiftChecklist }) {
  const { t } = useTranslation();
  const bad = r.items.filter((i) => !i.ok);
  if (bad.length === 0) return <span className="text-text-muted">{t("quality.sc.noRemarks")}</span>;
  return (
    <span className="text-danger">
      {bad.map((i) => (i.note ? `${i.object}: ${i.note}` : i.object)).join("; ")}
    </span>
  );
}
